import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { useLang } from "@/i18n";

const rise = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.12 },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = [t("hero_line1"), t("hero_line2"), t("hero_line3")];

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative min-h-screen w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1598408745613-178751e2ccde"
          alt="DMD GRUP kargo gemisi ile uluslararası lojistik"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-[#ffffff]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#ffffff] via-[#ffffff]/70 to-[#ffffff]/30" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 min-h-screen flex flex-col justify-end pb-20 pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-[#d81f2a]" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#d81f2a]">
            {t("hero_overline")}
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-[#0f1115] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={rise}
                initial="hidden"
                animate="show"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end gap-8 lg:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-xl font-body text-base md:text-lg text-[#5b6472] leading-relaxed"
          >
            {t("hero_sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-2 bg-[#d81f2a] text-[#ffffff] px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] font-bold hover:bg-white transition-colors"
            >
              {t("hero_cta")}
              <ArrowDownRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </a>
            <a
              href="#products"
              data-testid="hero-cta-2"
              className="inline-flex items-center gap-2 border border-black/15 text-[#0f1115] px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:border-[#d81f2a] hover:text-[#d81f2a] transition-colors"
            >
              {t("hero_cta2")}
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
